import apiService from '@sonifi/wb-core/services/api.service';
import {
  setAddExpanded,
  setExpandedRow,
  setTablePagination,
} from '@sonifi/wb-widget/store/tableConfig/tableConfigSlice';
import { TABLE_KEYS } from '@sonifi/wb-widget/store/tableConfig/tableConfigConstants';
import { setFormErrors } from '@sonifi/wb-widget/store/formErrors/formErrorsSlice';
import { FORM_ERRORS_IDS } from '@sonifi/wb-widget/store/formErrors/formErrorsConstants';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  buildApiUri,
  capitalizeString,
  parseQueryParams,
} from '@sonifi/wb-core/utils/parseHelper';
import { getGroups } from '@sonifi/wb-group/services/group.service';
import { PROPERTY_PARAMS } from '@sonifi/wb-group/constants/wbGroupConstants';
import { GROUPS_KEYS } from '@sonifi/wb-group/store/groupsConstants';
import { getUsersMapped } from '@sonifi/wb-admin/users/services/usersHelper';
import {
  PRODUCTS_SETTINGS_TO_SUBMIT,
  STAYCAST_BRANDING_KEYS,
} from '@sonifi/wb-admin/constants/wbAdminConstants';
import { translateFormErrors } from '@sonifi/wb-widget/components/FormErrorMessage/translateFormErrors';
import {
  setDefaultProperty,
  setLanguages,
  setLoadingProperties,
  setLoadingProperty,
  setLoadingSubmit,
  setProperties,
  setPropertyFormValues,
  setSyncUserProcessing,
  updateProperties,
} from '../../store/properties/propertiesSlice';
import {
  getUsersSplitted,
  getProductsMapped,
  getChildrenGroupsMapped,
  isCustomStaycast,
  getStaycastBrandingBody,
} from './propertiesHelper';
import { translateTokenResponse } from '@sonifi/wb-branding/services/staycastBranding.service';
import { defaultConfig } from '../../../../config-env';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';

const getProperties =
  ({
    page = 1,
    pageSize = 100,
    query,
    sortBy = 'cms_id',
    sortDir = 'asc',
  } = {}) =>
  async (dispatch) => {
    dispatch(setLoadingProperties(LOADING_STATUS.loading));
    const params = {
      page,
      rpp: pageSize,
      _query: query,
      _order_by: `${sortDir === 'desc' ? '-' : ''}${sortBy}`,
    };
    try {
      const url = `${ENDPOINTS.GROUPS}?${parseQueryParams(params)}`;
      const res = await apiService.get(url);
      const properties = res.data?.objects || [];
      dispatch(
        setTablePagination({
          page,
          pageSize,
          query,
          sortBy,
          sortDir,
          total: res.data?.total,
          tableKey: TABLE_KEYS.adminProperties,
        }),
      );

      const propertiesMapped = properties.map((p) => ({
        id: p.id,
        cms_id: p.cms_id,
        country: p.country,
        city: p.city,
        state: p.state,
        name: p.name,
      }));

      dispatch(setProperties({ properties: propertiesMapped }));
      dispatch(setLoadingProperties(LOADING_STATUS.success));
      return propertiesMapped;
    } catch (err) {
      console.error(err);
      dispatch(setLoadingProperties(LOADING_STATUS.error));
      return [];
    }
  };

const getPropertyById = (id) => async (dispatch, getState) => {
  try {
    const {
      groups: { groups },
      user: { permissions },
    } = getState();
    const usersParams = {
      groups__in: id,
      rpp: -1,
    };

    const group = groups[GROUPS_KEYS.allGroups].find(
      (g) => g.id === parseInt(id),
    );

    let groupChildrenMapped = [];

    if (group?.children?.length) {
      groupChildrenMapped = getChildrenGroupsMapped(
        groups[GROUPS_KEYS.allGroups],
        group.children,
      );
    }

    const usersUrl = `${ENDPOINTS.USERS}?${parseQueryParams(usersParams)}`;
    const users = await apiService.get(usersUrl);
    const usersMapped = getUsersMapped({
      groups: groups[GROUPS_KEYS.allGroups],
      permissions,
      users: users?.data?.objects,
    });
    const staycastConfig =
      group?.cms_id && (await dispatch(getStaycastConfig(group.cms_id)));

    const usersSplitted = getUsersSplitted(usersMapped);
    const property = {
      group: {
        ...group,
        childrenList: groupChildrenMapped,
        enabled_languages: group.enabled_languages?.length
          ? group.enabled_languages
          : [],
      },
      staycast: staycastConfig,
      users: usersSplitted,
    };

    dispatch(setPropertyFormValues({ property }));
    return property;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const synchronizeMarinaUsers = () => async (dispatch, getState) => {
  dispatch(setSyncUserProcessing({ syncUserProcessing: true }));
  const {
    adminProperties: {
      propertyFormValues: { group },
    },
  } = getState();
  const body = {
    type: 'UPDATE_MARINA_USERS',
    targets: [group.uri],
  };
  const url = ENDPOINTS.JOBS;
  await apiService.post(url, body);
  dispatch(setSyncUserProcessing({ syncUserProcessing: false }));
};

export const initRowExpanded = (id, add) => async (dispatch, getState) => {
  const {
    adminProperties: { properties },
  } = getState();
  dispatch(
    setAddExpanded({
      isExpanded: !!add,
      tableKey: TABLE_KEYS.adminProperties,
    }),
  );

  const propertyIndex = properties.findIndex((p) => p.id === parseInt(id));

  dispatch(
    setExpandedRow({
      expanded: propertyIndex !== -1 ? { [propertyIndex]: true } : {},
      tableKey: TABLE_KEYS.adminProperties,
    }),
  );
};

export const clearRowsAndForm = () => async (dispatch) => {
  dispatch(setDefaultProperty());
  dispatch(
    setAddExpanded({ isExpanded: false, tableKey: TABLE_KEYS.adminProperties }),
  );
  dispatch(
    setExpandedRow({ expanded: {}, tableKey: TABLE_KEYS.adminProperties }),
  );
};

export const loadProperties = (params) => async (dispatch) => {
  try {
    dispatch(getLanguages());
    await dispatch(getProperties(params));
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const loadProperty =
  ({ id, add }) =>
  async (dispatch) => {
    try {
      dispatch(setLoadingProperty(true));
      await dispatch(
        getGroups({
          groupKey: GROUPS_KEYS.propertyGroups,
          params: PROPERTY_PARAMS,
        }),
      );

      const productSettingsParams = {
        group_id: id,
        rpp: -1,
      };
      const productsParams = {
        enabled: true,
        rpp: -1,
      };
      const productsUrl = `${ENDPOINTS.PRODUCTS}?${parseQueryParams(
        productsParams,
      )}`;
      const productSettingsUrl = `${
        ENDPOINTS.PRODUCT_SETTINGS
      }?${parseQueryParams(productSettingsParams)}`;

      const [enabledProductsRes, productsSettingsRes] = await Promise.all([
        apiService.get(productsUrl),
        add
          ? Promise.resolve({ data: { objects: [] } })
          : apiService.get(productSettingsUrl),
        id ? dispatch(getPropertyById(id)) : Promise.resolve(null),
      ]);

      const enabledProducts = enabledProductsRes.data.objects;
      const productsSettings = productsSettingsRes.data.objects;

      const productsMapped = getProductsMapped({
        products: enabledProducts,
        productsSettings,
      });

      dispatch(
        setPropertyFormValues({
          property: {
            products: productsMapped,
          },
        }),
      );
      dispatch(setLoadingProperty(false));
    } catch (error) {
      console.error(error);
      dispatch(setLoadingProperty(false));
      return Promise.reject(error);
    }
  };

export const getStaycastConfig = (cmsId) => async () => {
  try {
    const staycastTokensApi = buildApiUri(
      defaultConfig.staycastApiUri,
      'welcome',
      'settings',
      'tokens',
    );
    const staycastTokenUrl = buildApiUri(staycastTokensApi, cmsId);
    const staycastConfigDefaultUrl = buildApiUri(
      staycastTokensApi,
      'defaults',
      'i18n_en-US',
    );
    const staycastConfigHyattUrl = buildApiUri(
      staycastTokensApi,
      'hyatt',
      'i18n_en-US',
    );
    const staycastConfigMarriottUrl = buildApiUri(
      staycastTokensApi,
      'marriott',
      'i18n_en-US',
    );

    // Implement this for Pairing Public Key
    const staycastKeyUrl = buildApiUri(
      defaultConfig.staycastApiUri,
      'welcome',
      'settings',
      'keys',
      cmsId,
    );
    const staycastKeyRes = await apiService
      .getRawAuth(staycastKeyUrl)
      .catch((_) => ({ data: { key: '' } }));

    const staycastConfig = await apiService.getRaw(staycastTokenUrl);
    const staycastConfigDefault = await apiService.getRaw(
      staycastConfigDefaultUrl,
    );
    const staycastConfigHyatt = await apiService.getRaw(staycastConfigHyattUrl);
    const staycastConfigMarriott = await apiService.getRaw(
      staycastConfigMarriottUrl,
    );
    const staycastConfigDefaultParsed = translateTokenResponse(
      defaultConfig.staycastApiUri,
      staycastConfigDefault,
    );
    const staycastConfigHyattParsed = translateTokenResponse(
      defaultConfig.staycastApiUri,
      staycastConfigHyatt,
    );
    const staycastConfigMarriottParsed = translateTokenResponse(
      defaultConfig.staycastApiUri,
      staycastConfigMarriott,
    );
    const staycastConfigParsed = translateTokenResponse(
      defaultConfig.staycastApiUri,
      staycastConfig,
    );

    const res = {
      defaults: staycastConfigDefaultParsed,
      hyatt: staycastConfigHyattParsed,
      marriott: staycastConfigMarriottParsed,
      tokens: staycastConfigParsed,
      preset: STAYCAST_BRANDING_KEYS.defaults,
      key: staycastKeyRes.data.key,
    };
    const isCustom = isCustomStaycast({
      selectedPreset: staycastConfigParsed,
      parentPreset: res[staycastConfigParsed.parent],
    });

    res.preset = isCustom
      ? STAYCAST_BRANDING_KEYS.tokens
      : staycastConfigParsed.parent;
    res.customData = {
      label: `Custom (${capitalizeString(staycastConfigParsed.parent)})`,
      parent: staycastConfigParsed.parent,
    };
    return res;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const submitForm = (payload) => async (dispatch, getState) => {
  const {
    adminProperties: { properties },
  } = getState();
  const { group, products } = payload;

  const {
    children,
    city,
    cms_id,
    country,
    dst,
    group_type,
    enabled_languages,
    market_type,
    name,
    notes,
    platform,
    phone,
    state,
    utc_offset,
    id,
  } = group;
  try {
    await dispatch(setLoadingSubmit({ loadingSubmit: true }));
    let newProperty = {};
    if (id) {
      newProperty = (
        await apiService.patch(`${ENDPOINTS.GROUPS}/${id}`, {
          children,
          city,
          cms_id,
          country,
          dst,
          group_type,
          id,
          enabled_languages: enabled_languages.map((l) => l.language),
          market_type,
          name,
          notes,
          platform,
          phone,
          state,
          utc_offset,
        })
      ).data;
      dispatch(updateProperties({ property: newProperty }));
      if (payload?.staycast) {
        const form = new FormData();
        const dataReq = getStaycastBrandingBody(payload);
        for (const [key, value] of Object.entries(dataReq)) {
          form.append(key, value);
        }
        const staycastTokensApi = buildApiUri(
          defaultConfig.staycastApiUri,
          'welcome',
          'settings',
          'tokens',
          cms_id,
        );
        await apiService
          .patchRawAuth(staycastTokensApi, form)
          .catch((e) => console.error(e));
        const staycastKeyUrl = buildApiUri(
          defaultConfig.staycastApiUri,
          'welcome',
          'settings',
          'keys',
          group.cms_id,
        );
        const formKey = new FormData();
        formKey.append('key', payload.staycast.key);
        payload.staycast.key &&
          (await apiService
            .patchRawAuth(staycastKeyUrl, formKey)
            .catch((e) => console.error(e)));
      }
    } else {
      newProperty = (
        await apiService.post(ENDPOINTS.GROUPS, {
          children,
          city,
          cms_id,
          country,
          dst,
          group_type,
          enabled_languages: enabled_languages.map((l) => l.language),
          market_type,
          name,
          notes,
          platform,
          phone,
          state,
          utc_offset,
        })
      ).data;

      dispatch(setProperties({ properties: [...properties, newProperty] }));
    }
    const productsSettingsParams = {
      group_id: newProperty.id,
      rpp: -1,
    };
    const productsSettings = (
      await apiService.get(
        `${ENDPOINTS.PRODUCT_SETTINGS}?${parseQueryParams(
          productsSettingsParams,
        )}`,
      )
    ).data.objects;

    for (const p in products) {
      const settings = productsSettings.find(
        (setting) => setting.product === products[p].uri,
      );
      if (settings) {
        const body = {};
        [
          ...PRODUCTS_SETTINGS_TO_SUBMIT.common,
          ...PRODUCTS_SETTINGS_TO_SUBMIT[p],
        ].forEach((prop) => {
          body[prop] = products[p].settings[prop];
        });
        body.id = settings?.id;
        body.group = settings?.group;
        body.uri = settings?.uri;
        await apiService.patch(
          `${ENDPOINTS.PRODUCT_SETTINGS}/${settings?.id}`,
          body,
        );
      }
    }
    // Update all Groups
    await dispatch(
      getGroups({
        groupKey: GROUPS_KEYS.allGroups,
      }),
    );
    return true;
  } catch (errors) {
    console.error(errors);
    dispatch(
      setFormErrors({
        id: FORM_ERRORS_IDS.adminPropertiesForm,
        errors: translateFormErrors(errors),
      }),
    );
  } finally {
    dispatch(setLoadingSubmit({ loadingSubmit: false }));
  }
};

export const getLanguages = () => async (dispatch) => {
  try {
    const url = 'languages';
    const res = await apiService.get(url);
    const languages = res.data.objects;
    await dispatch(setLanguages(languages));
  } catch (error) {
    console.error(error);
  }
};
